import { AddIcon, GroupIcon } from '../../icons';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, TextField, Typography } from '../../ui';

interface CreateGroupDialogProps {
  open: boolean;
  name: string;
  description: string;
  color: string;
  logoPreview: string | null;
  creating: boolean;
  /** Workspace name shown in the dialog subtitle. */
  targetName?: string;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onColorChange: (value: string) => void;
  onLogoChange: (file: File) => void;
  onLogoRemove: () => void;
  onClose: () => void;
  onCreate: () => void;
}

/**
 * Create-group dialog — captures the initial settings (name, description,
 * branding). Access rules + members come later on the group's edit page.
 * Fully controlled: all state and the create/upload flow live in the
 * orchestrator.
 */
export default function CreateGroupDialog({
  open,
  name,
  description,
  color,
  logoPreview,
  creating,
  targetName,
  onNameChange,
  onDescriptionChange,
  onColorChange,
  onLogoChange,
  onLogoRemove,
  onClose,
  onCreate,
}: CreateGroupDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <GroupIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              New group
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Inside {targetName || 'this workspace'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <TextField
            label="Group Name"
            fullWidth
            required
            autoFocus
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. North Region, Front Desk"
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Optional — shown on the workspace's groups list."
          />

          <Divider sx={{ my: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              Branding (optional)
            </Typography>
          </Divider>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <TextField
              label="Primary Color"
              fullWidth
              value={color}
              onChange={(e) => onColorChange(e.target.value)}
              placeholder="#3ea6d6"
              helperText="Hex color used when learners are scoped to this group"
            />
            {color && /^#[0-9A-Fa-f]{6}$/.test(color) && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  mt: '8px',
                  borderRadius: 1,
                  backgroundColor: color,
                  border: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight="bold" sx={{ width: 70, flexShrink: 0 }}>
              Logo
            </Typography>
            <Button variant="outlined" component="label" size="small">
              {logoPreview ? 'Replace' : 'Upload'} Logo
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onLogoChange(file);
                  }
                }}
              />
            </Button>
            {logoPreview && (
              <>
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo Preview"
                  sx={{
                    maxWidth: 120,
                    maxHeight: 40,
                    objectFit: 'contain',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                  }}
                />
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={onLogoRemove}
                >
                  Remove
                </Button>
              </>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Access rules (tags + provisioned content) and members are
            configured on the group's page after creation.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onCreate}
          disabled={creating}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          sx={{
            color: '#fff',
            '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
            px: 3,
          }}
        >
          Create Group
        </Button>
      </DialogActions>
    </Dialog>
  );
}
